/**
 * product-variants.js — TFD Shopify Theme
 * Handles color select dropdown, variant switching,
 * image/price/SKU updates, URL management, and
 * quantity-aware pre-order / stock status messages.
 */
(function () {
  'use strict';

  var product = window.productData;
  if (!product) return;

  // Build a variant lookup by ID
  var variantsById = {};
  product.variants.forEach(function (v) {
    variantsById[v.id] = v;
  });

  // DOM references — one selector per option with multiple values
  var variantSelects = document.querySelectorAll('.variant-select__input');
  var variantIdInput = document.querySelector('input[name="id"]');
  var skuEl = document.querySelector('[data-product-sku]');
  var priceEl = document.querySelector('[data-product-price]');
  var comparePriceEl = document.querySelector('[data-product-compare-price]');
  var mainImage = document.querySelector('[data-product-image]');
  var qtyInput = document.querySelector('input[name="quantity"]');
  var addBtn = document.querySelector('.add-to-cart__button');
  var statusEl = document.querySelector('[data-cart-status]');
  var noteInput = document.querySelector('[data-preorder-note]');

  // Note: no early return when there are no option selectors —
  // single-variant products still need the qty listeners and
  // updateStatus() for pre-order button/status/note handling.

  var currentVariant = null;

  /**
   * Collect the currently-selected value for each option index.
   * Single-value options aren't rendered as selectors, so fall back to the
   * value from the first variant for any option index without a selector.
   */
  function selectedOptions() {
    var firstVariantOpts = (product.variants[0] && product.variants[0].options) || [];
    var opts = firstVariantOpts.slice();
    variantSelects.forEach(function (sel) {
      var idx = parseInt(sel.dataset.optionIndex, 10);
      if (!isNaN(idx)) opts[idx] = sel.value;
    });
    return opts;
  }

  /**
   * Find the variant matching all currently-selected option values.
   */
  function findVariant() {
    var selected = selectedOptions();
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var match = true;
      for (var j = 0; j < selected.length; j++) {
        if (v.options[j] !== selected[j]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  /**
   * Format cents to a money string.
   */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  /**
   * Determine whether a variant is a pre-order item.
   * A variant is preorder when it sells past zero stock:
   * inventory is tracked by Shopify, policy is "continue",
   * and current inventory is at or below zero.
   */
  function isVariantPreorder(variant) {
    return variant.available &&
           variant.inventory_management === 'shopify' &&
           variant.inventory_policy === 'continue' &&
           variant.inventory_quantity !== undefined &&
           variant.inventory_quantity <= 0;
  }

  /**
   * Whether a variant can partially pre-order (has some stock
   * but accepts orders beyond that stock via "continue" policy).
   */
  function canPartPreorder(variant) {
    return variant.available &&
           variant.inventory_management === 'shopify' &&
           variant.inventory_policy === 'continue' &&
           variant.inventory_quantity !== undefined &&
           variant.inventory_quantity > 0;
  }

  /**
   * Update button text and status message based on variant
   * state and requested quantity.
   */
  function updateStatus() {
    if (!currentVariant) return;

    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var variant = currentVariant;
    var preorder = isVariantPreorder(variant);
    var partPre = canPartPreorder(variant);
    var avail = variant.inventory_quantity || 0;

    // Button state
    if (addBtn) {
      if (!variant.available) {
        addBtn.disabled = true;
        addBtn.textContent = 'Sold Out';
      } else if (preorder) {
        addBtn.disabled = false;
        addBtn.textContent = 'Pre-Order';
      } else {
        addBtn.disabled = false;
        addBtn.textContent = 'Add to Cart';
      }
    }

    // Whether stock is limited (policy is "deny", so can't oversell)
    var limited = variant.available &&
                  variant.inventory_management === 'shopify' &&
                  variant.inventory_policy === 'deny' &&
                  avail > 0;

    // Shared pre-order copy — statusEl shows it on the page, noteInput
    // carries it into the order as a line item property. Mirrored by the
    // Liquid initial state in add-to-cart.liquid.
    var SHIPS_IN = '(Pre-order typically ships in 12 weeks or less)';
    var preorderMsg = '';
    if (variant.available) {
      if (preorder) {
        preorderMsg = 'Preorder ' + SHIPS_IN;
      } else if (partPre && qty > avail) {
        preorderMsg = avail + ' in stock, ' + (qty - avail) + ' preorder ' + SHIPS_IN;
      }
    }

    // Status message
    if (statusEl) {
      if (!variant.available) {
        statusEl.innerHTML = 'To order this item please contact us at ' +
          '<a href="mailto:info@tf.design">info@tf.design</a>';
      } else if (preorderMsg) {
        statusEl.textContent = preorderMsg;
      } else if (limited && qty > avail) {
        statusEl.textContent = 'Stock limited to ' + avail;
      } else {
        statusEl.textContent = '';
      }
    }

    // Pre-order line item property (see add-to-cart.liquid). Disabled
    // inputs are left out of the add-to-cart FormData entirely.
    if (noteInput) {
      noteInput.value = preorderMsg;
      noteInput.disabled = !preorderMsg;
    }
  }

  /**
   * Update all product displays for the selected variant.
   */
  function updateVariant(variant) {
    if (!variant) return;
    currentVariant = variant;

    // Update hidden variant ID input (for add-to-cart form)
    if (variantIdInput) {
      variantIdInput.value = variant.id;
    }

    // Update SKU
    if (skuEl) {
      skuEl.textContent = variant.sku || '';
    }

    // Update price
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }

    // Update compare-at price
    if (comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.style.display = 'none';
      }
    }

    // Update main product image (skip if a thumbnail click is already handling it)
    if (mainImage && variant.featured_image && !mainImage.dataset.thumbSyncing) {
      mainImage.src = variant.featured_image.src;
      mainImage.alt = variant.featured_image.alt || variant.title;
    }

    // Sync active thumbnail to match the selected variant
    var thumbs = document.querySelectorAll('.product-detail__thumbnail');
    if (thumbs.length) {
      thumbs.forEach(function(t) {
        t.classList.remove('product-detail__thumbnail--active');
        if (t.dataset.variantId && t.dataset.variantId === String(variant.id)) {
          t.classList.add('product-detail__thumbnail--active');
        }
      });
    }

    // Update browser URL without reload (only when there's an actual
    // variant choice — keeps single-variant product URLs clean)
    if (variantSelects.length && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Update button + status
    updateStatus();
  }

  /**
   * Handle select change events on any option selector.
   */
  variantSelects.forEach(function (sel) {
    sel.addEventListener('change', function () {
      updateVariant(findVariant());
    });
  });

  /**
   * Handle quantity changes — re-evaluate status message
   * since part-preorder depends on requested qty vs stock.
   */
  if (qtyInput) {
    qtyInput.addEventListener('input', updateStatus);
    qtyInput.addEventListener('change', updateStatus);
  }

  // On page load: sync image/SKU/price to the currently selected variant
  (function initFromUrl() {
    var url = new URL(window.location.href);
    var variantParam = url.searchParams.get('variant');
    if (variantParam && variantsById[variantParam]) {
      var v = variantsById[variantParam];
      // Sync every option selector to this variant's option values
      variantSelects.forEach(function (sel) {
        var idx = parseInt(sel.dataset.optionIndex, 10);
        if (!isNaN(idx) && v.options[idx] !== undefined) {
          sel.value = v.options[idx];
        }
      });
      updateVariant(v);
    } else {
      // No URL param — sync to whichever values the selectors currently show
      var v = findVariant();
      if (v) updateVariant(v);
    }
  })();
})();
